import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import {
  ExperimentsBag,
  ExperimentsInitParams,
} from '@wix/cashier-common/dist/src/utils/experimentsService';
import { validateGUID } from '@wix/cashier-common/dist/src/utils/guid/validateGUID';
import { InternalPaymentsWidgetProps } from '../../../types/InternalPaymentsWidgetProps';
import { wixSdkAdapter } from '../../../utils/wixSdkAdapter';

export const buildConfig = (props: InternalPaymentsWidgetProps) => {
  const config = {
    get visitorId(): string {
      let visitorId: string = props.configuration?.visitorId;

      if (!validateGUID(visitorId)) {
        visitorId = wixSdkAdapter.getVisitorId();
      }

      return visitorId;
    },
    get siteOwnerId(): string {
      let siteOwnerId: string = props.configuration?.siteOwnerId;

      if (!validateGUID(siteOwnerId)) {
        siteOwnerId = wixSdkAdapter.getSiteOwnerId();
      }

      return siteOwnerId;
    },
    get metaSiteId(): string {
      let metaSiteId: string = props.configuration?.msid;

      if (!validateGUID(metaSiteId)) {
        metaSiteId = wixSdkAdapter.getMetasiteId() || undefined;
      }

      return metaSiteId;
    },
    get isUserSignedIn(): boolean {
      return (
        props.configuration.isSignedInUser ??
        wixSdkAdapter.isUserOfUserSignedIn()
      );
    },
    get isLiveSiteMode(): boolean {
      return props.configuration.viewMode
        ? props.configuration.viewMode === 'Site'
        : wixSdkAdapter.getViewMode() === 'site';
    },
    get experimentsInitParams(): ExperimentsInitParams {
      const initParams: ExperimentsInitParams = {};

      if (config.overrides.experiments) {
        initParams.experiments = config.overrides.experiments;

        return initParams;
      }

      const { widgetSettings } = props;
      const experiments = widgetSettings.experiments;

      if (experiments && Object.keys(experiments).length !== 0) {
        initParams.experiments = experiments;

        return initParams;
      }

      const visitorId: string = config.visitorId;
      const siteOwnerId: string = config.siteOwnerId;

      if (visitorId && siteOwnerId) {
        initParams.forSiteVisitors = {
          visitorId,
          siteOwnerId,
        };
      }

      return initParams;
    },
    get cashierAppInstanceId(): string {
      return props.widgetSettings.cashierAppInstanceId || null;
    },
    get widgetVersion(): string {
      return props.widgetSettings.widgetVersion;
    },
    get widgetStaticsBaseUrl(): string {
      let baseUrl: string;

      if (props.widgetSettings) {
        baseUrl = props.widgetSettings.widgetStaticsBaseUrl;
      }

      if (baseUrl) {
        if (baseUrl.startsWith('//')) {
          baseUrl = document.location.protocol + baseUrl;
        }

        return baseUrl;
      }

      return '/';
    },
    get preferNewTabOverRedirect(): boolean {
      // If any of these flags are "true" - it means TPA doesn't put PaymentResult URL into the Order,
      // and we can't use SAME_WINDOW approach (even if other flag is "false")
      return props.walletPaymentInNewTab || props.creditCardPaymentInNewTab;
    },
    get singlePaymentMethod(): PaymentMethod {
      if (props.tokenizeMode) {
        return PaymentMethod.CreditCard;
      }

      return props.singlePaymentMethod;
    },
    get apiBaseUrl(): string {
      return props.widgetSettings.dev?.apiBaseUrl || props.host;
    },
    overrides: {
      get ccFormUrl(): string | undefined {
        return (
          props.configuration?.cashierPCIUrl ||
          props.widgetSettings.dev?.ccFormUrl
        );
      },
      get experiments(): ExperimentsBag | undefined {
        let overrides: ExperimentsBag;

        if (props.experiments) {
          overrides = { ...props.experiments };
        }

        if (props.widgetSettings.dev?.experiments) {
          overrides = {
            ...overrides,
            ...props.widgetSettings.dev.experiments,
          };
        }

        return overrides;
      },
    },
  };

  return config;
};
